"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { TweetContainer, enrichTweet, useTweet } from "react-tweet";
import type { Tweet } from "react-tweet/api";
import styles from "./ExpandableTweetPreview.module.css";
import CompactControls from "./ExpandableTweetPreviewCompactControls";
import CompactTweetContent from "./ExpandableTweetPreviewCompactContent";
import ExpandedTweetContent from "./ExpandableTweetPreviewExpandedContent";
import MeasurementShell from "./ExpandableTweetPreviewMeasurementShell";
import type { EnrichedTweet } from "./ExpandableTweetPreview.types";
import type { TweetPreviewMode } from "./TweetPreviewModeContext";
interface ExpandableTweetPreviewProps {
  readonly href: string;
  readonly tweetId: string;
  readonly compactHeightClassName?: string | undefined;
  readonly compactMode?: TweetPreviewMode | undefined;
  readonly renderFallback?: ((href: string) => ReactNode) | undefined;
}
const COMPACT_PX = 256;
const COMPACT_THRESHOLD_MULTIPLIER = 1.5;
const COMPACT_THRESHOLD_PX = COMPACT_PX * COMPACT_THRESHOLD_MULTIPLIER;
const normalizeTweetEntities = (entities: Tweet["entities"] | null | undefined): Tweet["entities"] => ({
  hashtags: Array.isArray(entities?.hashtags) ? entities.hashtags : [],
  urls: Array.isArray(entities?.urls) ? entities.urls : [],
  user_mentions: Array.isArray(entities?.user_mentions) ? entities.user_mentions : [],
  symbols: Array.isArray(entities?.symbols) ? entities.symbols : [],
  ...(Array.isArray(entities?.media) ? {
    media: entities.media
  } : {})
});
export const normalizeTweetForEnrichment = (tweet: Tweet): Tweet => {
  const normalizedTweet = {
    ...tweet,
    entities: normalizeTweetEntities(tweet.entities)
  };
  if (!tweet.quoted_tweet) {
    return normalizedTweet;
  }
  return {
    ...normalizedTweet,
    quoted_tweet: {
      ...tweet.quoted_tweet,
      entities: normalizeTweetEntities(tweet.quoted_tweet.entities)
    }
  };
};
const enrichTweetSafely = (tweet: Tweet): EnrichedTweet | null => {
  try {
    return enrichTweet(normalizeTweetForEnrichment(tweet));
  } catch {
    return null;
  }
};
const LoadingPlaceholder = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-h-full tw-w-full tw-animate-pulse tw-items-center tw-justify-center tw-rounded-xl tw-bg-iron-900/60 tw-text-iron-300">Loading tweet...</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default function ExpandableTweetPreview(t0) {
  const $ = _c(6);
  const {
    href,
    tweetId,
    compactHeightClassName,
    compactMode,
    renderFallback
  } = t0;
  let t1;
  if ($[0] !== compactHeightClassName || $[1] !== compactMode || $[2] !== href || $[3] !== renderFallback || $[4] !== tweetId) {
    t1 = <ExpandableTweetPreviewInner key={tweetId} href={href} tweetId={tweetId} compactHeightClassName={compactHeightClassName} compactMode={compactMode} renderFallback={renderFallback} />;
    $[0] = compactHeightClassName;
    $[1] = compactMode;
    $[2] = href;
    $[3] = renderFallback;
    $[4] = tweetId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function ExpandableTweetPreviewInner(t0) {
  const $ = _c(33);
  const {
    href,
    tweetId,
    compactHeightClassName: t1,
    compactMode,
    renderFallback
  } = t0;
  const compactHeightClassName = t1 === undefined ? "tw-h-64" : t1;
  const {
    data,
    isLoading
  } = useTweet(tweetId);
  let t2;
  if ($[0] !== data) {
    t2 = data ? enrichTweetSafely(data) : null;
    $[0] = data;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tweet = t2;
  const [isExpanded, setIsExpanded] = useState(false);
  const [measuredHeight, setMeasuredHeight] = useState(null);
  const measureRef = useRef(null);
  const allowCompact = (compactMode ?? "auto") === "auto";
  let t3;
  let t4;
  if ($[2] !== allowCompact || $[3] !== measuredHeight || $[4] !== tweet) {
    t3 = () => {
      if (!allowCompact) {
        return;
      }
      if (!tweet || measuredHeight !== null) {
        return;
      }
      if (!measureRef.current) {
        return;
      }
      const rect = measureRef.current.getBoundingClientRect();
      if (rect.height > 0) {
        setMeasuredHeight(rect.height);
      }
    };
    t4 = [tweet, measuredHeight, allowCompact];
    $[2] = allowCompact;
    $[3] = measuredHeight;
    $[4] = tweet;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useLayoutEffect(t3, t4);
  const shouldCompact = allowCompact && (measuredHeight === null || measuredHeight > COMPACT_THRESHOLD_PX);
  const heightClassName = !shouldCompact || isExpanded ? "" : compactHeightClassName;
  const modeClassName = isExpanded ? styles.expanded : styles.compact;
  const overflowClassName = isExpanded || !shouldCompact ? "tw-overflow-visible" : "tw-overflow-hidden";
  const rootClassName = `${styles.root ?? ""} ${modeClassName ?? ""} ${heightClassName} ${overflowClassName} tw-relative tw-w-full tw-min-w-0 tw-max-w-full`;
  const showMeasure = allowCompact && measuredHeight === null;
  const showExpanded = isExpanded || !shouldCompact;
  const showCompactControls = allowCompact && !isExpanded && shouldCompact;
  if (isLoading) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <TweetContainer className="tw-h-full tw-w-full tw-min-w-0 tw-max-w-full"><LoadingPlaceholder /></TweetContainer>;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== rootClassName) {
      t6 = <div className={rootClassName} data-theme="dark">{t5}</div>;
      $[8] = rootClassName;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  if (!tweet) {
    let t5;
    if ($[10] !== href || $[11] !== renderFallback) {
      t5 = renderFallback ? renderFallback(href) : null;
      $[10] = href;
      $[11] = renderFallback;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== rootClassName || $[14] !== t5) {
      t6 = <div className={rootClassName} data-theme="dark">{t5}</div>;
      $[13] = rootClassName;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  let t5;
  if ($[16] !== tweet) {
    t5 = <ExpandedTweetContent tweet={tweet} />;
    $[16] = tweet;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== showMeasure || $[19] !== t5) {
    t6 = <MeasurementShell show={showMeasure} measureRef={measureRef}>{t5}</MeasurementShell>;
    $[18] = showMeasure;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== showExpanded || $[22] !== tweet) {
    t7 = <TweetContainer className="tw-h-full tw-w-full tw-min-w-0 tw-max-w-full">{showExpanded ? <ExpandedTweetContent tweet={tweet} /> : <CompactTweetContent tweet={tweet} />}</TweetContainer>;
    $[21] = showExpanded;
    $[22] = tweet;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsExpanded(true);
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isExpanded || $[26] !== showCompactControls) {
    t9 = <CompactControls showFade={showCompactControls} showButton={showCompactControls} isExpanded={isExpanded} onExpand={t8} />;
    $[25] = isExpanded;
    $[26] = showCompactControls;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== rootClassName || $[29] !== t6 || $[30] !== t7 || $[31] !== t9) {
    t10 = <div className={rootClassName} data-theme="dark">{t6}{t7}{t9}</div>;
    $[28] = rootClassName;
    $[29] = t6;
    $[30] = t7;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}